<template>
  <div class="area-distribution">
    <div class="area-distribution__header">
      <span class="area-distribution__title">Distribución del área</span>
      <div class="area-distribution__mode-toggle">
        <button
          class="area-distribution__mode-pill"
          :class="{ 'area-distribution__mode-pill--active': mode === 'area' }"
          @click="mode = 'area'"
        >m²</button>
        <button
          class="area-distribution__mode-pill"
          :class="{ 'area-distribution__mode-pill--active': mode === 'count' }"
          @click="mode = 'count'"
        >Naves</button>
      </div>
      <span class="area-distribution__total">{{ mode === 'area' ? totalAreaLabel : totalCountLabel }}</span>
    </div>

    <div class="area-distribution__bar">
      <div
        v-for="seg in segments"
        :key="seg.status"
        class="area-distribution__segment"
        :class="{ 'area-distribution__segment--dimmed': isDimmed(seg.status) }"
        :style="{ width: seg.percent + '%', background: seg.color }"
        @mouseenter="onSegmentHover(seg)"
        @mousemove="onSegmentMove"
        @mouseleave="onSegmentLeave"
        @click="onSegmentClick(seg)"
      ></div>
    </div>

    <div
      v-if="hoveredSegment"
      class="area-distribution__tooltip"
      :style="{ left: tooltipPos.x + 'px', top: tooltipPos.y + 'px' }"
    >
      <span class="area-distribution__tooltip-label">{{ hoveredSegment.label }}</span>
      <span class="area-distribution__tooltip-value">
        {{ hoveredSegment.percent.toFixed(2) }}% · {{ mode === 'area' ? formatArea(hoveredSegment.area) : formatCount(hoveredSegment.count) }}
      </span>
    </div>

    <div class="area-distribution__legend">
      <div
        v-for="seg in segments"
        :key="seg.status"
        class="area-distribution__legend-item"
        :class="{ 'area-distribution__legend-item--dimmed': isDimmed(seg.status) }"
      >
        <span class="area-distribution__legend-dot" :style="{ background: seg.color }"></span>
        <span class="area-distribution__legend-label">{{ seg.label }}</span>
        <span class="area-distribution__legend-percent" :style="{ color: seg.color }">
          {{ Math.round(seg.percent) }}%
        </span>
        <span class="area-distribution__legend-area">({{ mode === 'area' ? formatArea(seg.area) : formatCount(seg.count) }})</span>      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'

const props = defineProps({
  plots: { type: Array, default: () => [] },
  activeFilter: { type: String, default: null },
})

const emit = defineEmits(['update:activeFilter'])

// Mismo orden siempre, para que la barra se lea consistente entre visitas
// en vez de reordenarse según qué estatus tenga más área.
const STATUS_ORDER = ['OCCUPIED', 'RESERVED', 'VACANT', 'MAINTENANCE']

const STATUS_META = {
  OCCUPIED: { label: 'Ocupada', color: '#10b981' },
  RESERVED: { label: 'En negociación', color: '#f0a500' },
  VACANT: { label: 'Disponible', color: '#3b82f6' },
  MAINTENANCE: { label: 'Mantenimiento', color: '#94a3b8' },
}

const mode = ref('area') // 'area' | 'count'

const totalArea = computed(() => props.plots.reduce((sum, p) => sum + (p.areaM2 || 0), 0))
const totalAreaLabel = computed(() => formatArea(totalArea.value))
const totalCountLabel = computed(() => formatCount(props.plots.length))

function formatArea(value) {
  return `${Math.round(value || 0).toLocaleString('es-MX')} m²`
}

function formatCount(value) {
  const n = value || 0
  return `${n} nave${n === 1 ? '' : 's'}`
}

const segments = computed(() => {
  const areaByStatus = {}
  const countByStatus = {}
  for (const plot of props.plots) {
    areaByStatus[plot.status] = (areaByStatus[plot.status] || 0) + (plot.areaM2 || 0)
    countByStatus[plot.status] = (countByStatus[plot.status] || 0) + 1
  }
  const totalCount = props.plots.length
  return STATUS_ORDER.map((status) => {
    const area = areaByStatus[status] || 0
    const count = countByStatus[status] || 0
    const percent = mode.value === 'area'
      ? (totalArea.value === 0 ? 0 : (area / totalArea.value) * 100)
      : (totalCount === 0 ? 0 : (count / totalCount) * 100)
    return { status, area, count, percent, ...STATUS_META[status] }
  })
})

const hoveredStatus = ref(null)
const tooltipPos = reactive({ x: 0, y: 0 })

const hoveredSegment = computed(() => segments.value.find((s) => s.status === hoveredStatus.value) || null)

// Un segmento se atenúa si hay un filtro activo y no es el elegido, o si no
// hay filtro pero se está pasando el mouse sobre otro segmento distinto.
function isDimmed(status) {
  if (props.activeFilter) return props.activeFilter !== status
  if (hoveredStatus.value) return hoveredStatus.value !== status
  return false
}

function onSegmentHover(seg) {
  hoveredStatus.value = seg.status
}

function onSegmentMove(e) {
  const rect = e.currentTarget.closest('.area-distribution').getBoundingClientRect()
  tooltipPos.x = e.clientX - rect.left + 12
  tooltipPos.y = e.clientY - rect.top + 12
}

function onSegmentLeave() {
  hoveredStatus.value = null
}

function onSegmentClick(seg) {
  emit('update:activeFilter', props.activeFilter === seg.status ? null : seg.status)
}
</script>

<style scoped>
.area-distribution {
  background: var(--bg-surface-1);
  border: 1px solid var(--border-active);
  border-radius: 10px;
  padding: 1rem 1.1rem;
  position: relative;
}

.area-distribution__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.6rem;
}

.area-distribution__title {
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--text-secondary);
}

.area-distribution__total {
  font-family: var(--font-mono);
  font-size: 0.7rem;
  color: var(--text-secondary);
}

.area-distribution__mode-toggle {
  display: flex;
  gap: 0.25rem;
  margin-left: auto;
  margin-right: 0.6rem;
}

.area-distribution__mode-pill {
  background: transparent;
  border: 1px solid var(--border-active);
  border-radius: 20px;
  padding: 0.1rem 0.5rem;
  font-family: var(--font-body);
  font-size: 0.62rem;
  color: var(--text-secondary);
  cursor: pointer;
}

.area-distribution__mode-pill--active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: #ffffff;
}

.area-distribution__bar {
  display: flex;
  width: 100%;
  height: 12px;
  border-radius: 6px;
  overflow: hidden;
}

.area-distribution__segment {
  height: 100%;
  cursor: pointer;
  opacity: 1;
  transition: opacity 0.15s ease;
}

.area-distribution__segment--dimmed {
  opacity: 0.35;
}

.area-distribution__tooltip {
  position: absolute;
  background: var(--bg-surface-1);
  border: 1px solid var(--border-active);
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  pointer-events: none;
  z-index: 5;
  white-space: nowrap;
}

.area-distribution__tooltip-label {
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 0.75rem;
  color: var(--text-content);
}

.area-distribution__tooltip-value {
  font-family: var(--font-mono);
  font-size: 0.7rem;
  color: var(--text-secondary);
}

.area-distribution__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.7rem;
}

.area-distribution__legend-item {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  transition: opacity 0.15s ease;
}

.area-distribution__legend-item--dimmed {
  opacity: 0.4;
}

.area-distribution__legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}

.area-distribution__legend-label {
  font-family: var(--font-body);
  font-size: 0.7rem;
  color: var(--text-secondary);
}

.area-distribution__legend-percent {
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 0.7rem;
}

.area-distribution__legend-area {
  font-family: var(--font-mono);
  font-size: 0.65rem;
  color: var(--text-secondary);
}
</style>